// IUnorderedArrivalResponseからIUnorderedArrivalResultへの変換を行う

import { IRecommendOrderArrival } from '@/recommendOrderModule/domains/entities/recommendOrderArrival';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { IAnnounce, IAnnounceDaily } from '@/coreDomainsModule/entities/forecast';
import { ISalesResult } from '@/coreDomainsModule/entities/salesResults';
import { BusinessFactory } from '@/coreDomainsModule/factories/BusinessFactory';
import { DateService } from '@/coreDomainsModule/services/dateService';
import {
  Arrival,
  CustomerAnnounceSummary,
  CustomerAnnounceDaily,
  GetSimulatedAggregateBusinessInfo,
  Inventory,
  Order,
  PartialDelivery,
  SalesResultSummary,
  SupplierAnnounce,
  UserData,
} from '@/coreInfrastructureModule/axios/generated/aggregateBusinessManage/api';
import { convertUnixTimeToIDate } from '@/corePagesModule/BaseTableContent/utils/dateFormat';
import { findOrderStatusOrNull, IArrival, OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { getRandomStr } from '@/businessRepository/idUtils';
import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';
import { IDate } from '@/coreDomainsModule/date';
import { RECOMMENDED_ORDER_DATE_WHEN_NULL } from '../constants';
import { IMvpInventory } from '@/coreDomainsModule/entities/inventory';
import { UserAccountFactory } from '@/coreDomainsModule/factories/userAccountFactory';

/**
 *　repositoryの型をUI用に変換する
 */
export class SimulateBusinessAdapter {
  /**
   * SalesResultSummary[]からISalesResult[]へ変換する
   * @param response - SalesResultSummary
   */
  private static adaptSalesResult = (response?: SalesResultSummary[]): ISalesResult[] => {
    if (!response) return [];
    const salesResults: ISalesResult[] = [];
    response.map((result) => {
      salesResults.push({
        year: result.targetYear,
        month: result.targetMonth,
        amount: result.salesResultAmount,
      });
    });
    return salesResults;
  };

  /**
   * Arrival[]からIRecommendOrderArrival[]へ変換する
   * TODO: ここでの入荷って、入荷実績な気がしているので一旦空で返しています
   * @param leadTime - リードタイム
   * @param response - Arrival
   */
  private static adaptArrival = (leadTime: number, response?: Arrival[]): IRecommendOrderArrival[] => {
    return [];
    // if (!response) return [];
    // const arrivals: IRecommendOrderArrival[] = [];
    // response.map((arrival) => {
    //   arrivals.push({
    //     displayId: getRandomStr(),
    //     arrivalId: arrival.arrivalId,
    //     year: arrival.targetYear,
    //     month: arrival.targetMonth,
    //     day: 1, // TODO: dayはdbで管理していませんがどうしましょうか？
    //     amount: arrival.arrivalAmount,
    //     leadTime: leadTime,
    //   });
    // });
    // return arrivals;
  };

  /**
   * CustomerAnnounceSummary[]からIAnnounce[]へ変換する
   * @param response - CustomerAnnounceSummary
   */
  private static adaptCustomerAnnounce = (response?: CustomerAnnounceSummary[]): IAnnounce[] => {
    if (!response) return [];
    const announces: IAnnounce[] = [];
    response.map((announce) => {
      announces.push({
        year: announce.customerForecastTargetYear,
        month: announce.customerForecastTargetMonth,
        amount: announce.customerForecastAmountTotal,
        manageInitiatedYearMonth: {
          year: announce.customerForecastReceivedYear,
          month: announce.customerForecastReceivedMonth,
        },
        createAt: undefined,
        updateAt: undefined,
      });
    });
    return announces;
  };

  /**
   * CustomerAnnounceDaily[]からIAnnounceDaily[]へ変換する
   * @param response - CustomerAnnounceDaily
   */
  private static adaptCustomerAnnounceDaily = (response?: CustomerAnnounceDaily[]): IAnnounceDaily[] => {
    if (!response) return [];
    const announces: IAnnounceDaily[] = [];
    response.map((announce) => {
      announces.push({
        year: announce.customerForecastTargetYear,
        month: announce.customerForecastTargetMonth,
        day: announce.customerForecastTargetDay,
        amount: announce.customerForecastAmount,
        manageInitiatedYearMonth: {
          year: announce.customerForecastReceivedYear,
          month: announce.customerForecastReceivedMonth,
        },
        createAt: undefined,
        updateAt: undefined,
      });
    });
    return announces;
  };

  /**
   * SupplierAnnounce[]からIAnnounce[]へ変換する
   * @param response - SupplierAnnounce
   */
  private static adaptSupplierAnnounce = (response?: SupplierAnnounce[]): IAnnounce[] => {
    if (!response) return [];
    const announces: IAnnounce[] = [];
    response.map((announce) => {
      announces.push({
        year: announce.supplierForecastTargetYear,
        month: announce.supplierForecastTargetMonth,
        amount: announce.supplierForecastAmount,
        manageInitiatedYearMonth: {
          year: announce.supplierForecastCreatedYear,
          month: announce.supplierForecastCreatedMonth,
        },
        createAt: undefined,
        updateAt: undefined,
      });
    });
    return announces;
  };

  /**
   * Order[]からIRecommendOrderArrival[]へ変換する
   * TODO 入荷予定日なのか、リードタイム分の日数を減算した日付なのか確認（今はとりあえず入荷予定日としている）
   * @param leadTime - リードタイム
   * @param response - Order
   */
  private static adaptOrderToIRecommendOrderArrival = (leadTime: number, response?: Order[]): IRecommendOrderArrival[] => {
    if (!response) return [];
    const arrivals: IRecommendOrderArrival[] = [];
    response.map((order) => {
      const createdOrderDate: IDate = order.createdOrderDate
        ? convertUnixTimeToIDate(order.createdOrderDate)
        : RECOMMENDED_ORDER_DATE_WHEN_NULL;
      const estimatedTimeOfArrivalDate = convertUnixTimeToIDate(order.estimatedTimeOfArrival);
      arrivals.push({
        displayId: getRandomStr(),
        arrivalId: order.orderId,
        year: createdOrderDate.year,
        month: createdOrderDate.month,
        day: createdOrderDate.day,
        crossDate: estimatedTimeOfArrivalDate,
        amount: order.orderAmount,
        leadTime: leadTime,
        status: findOrderStatusOrNull(order.orderStatus) ?? OrderStatus.Unknown,
        isFixOrdered: true, // DBにはそもそも確定発注しかいれないので
        memoText: order.orderNote,
        number: order.orderNumber,
        updateAt: order.updateAt,
        orderGroupId: order.orderGroupId,
        orderGroupName: order.orderGroupName,
        orderGroupUpperLimitQuantity: order.upperLimitQuantity,
        reminderMailDataId: order.reminderMailDataId,
        fixOrderDate: undefined,
        partialDeliveries: order.partialDeliveries ? this.adaptPartialDeliveryToIArrival(leadTime, order.partialDeliveries) : [],
      });
    });
    return arrivals;
  };

  /**
   * PartialDelivery[]からIArrival[]へ変換する
   * TODO 入荷予定日なのか、リードタイム分の日数を減算した日付なのか確認（今はとりあえず入荷予定日としている）
   * @param leadTime - リードタイム
   * @param response - PartialDelivery
   */
  private static adaptPartialDeliveryToIArrival = (leadTime: number, response?: PartialDelivery[]): IArrival[] => {
    if (!response) return [];
    const arrivals: IRecommendOrderArrival[] = [];
    response.map((partialDelivery) => {
      const createdOrderDate: IDate = partialDelivery.createdPartialDeliveryDate
        ? convertUnixTimeToIDate(partialDelivery.createdPartialDeliveryDate)
        : RECOMMENDED_ORDER_DATE_WHEN_NULL;
      const estimatedTimeOfArrivalDate = convertUnixTimeToIDate(partialDelivery.partialDeliveryEstimatedTimeOfArrival);
      arrivals.push({
        displayId: getRandomStr(),
        arrivalId: partialDelivery.partialDeliveryId,
        year: createdOrderDate.year,
        month: createdOrderDate.month,
        day: createdOrderDate.day,
        crossDate: estimatedTimeOfArrivalDate,
        amount: partialDelivery.partialDeliveryAmount,
        leadTime: leadTime,
        status: findOrderStatusOrNull(partialDelivery.partialDeliveryState) ?? OrderStatus.Unknown,
        isFixOrdered: true, // DBにはそもそも確定発注しかいれないので
        memoText: undefined,
        fixOrderDate: undefined,
        partialDeliveries: undefined, // 分納の情報はここでは不要
        number: partialDelivery.partialDeliveryNumber,
        updateAt: partialDelivery.updateAt,
      });
    });
    return arrivals;
  };

  /**
   * Inventory[]からnumberへ変換する
   * 在庫年月日が最新のものかつ、今月または先月、先々月の在庫情報を取得する(なければundefinedを返す)
   * @param receiveExecutionDate - 在庫年月日
   * @param response - inventory
   */
  private static adaptLatestInventory = (receiveExecutionDate: Date, response?: Inventory[]): Inventory | undefined => {
    if (!response) return undefined;
    const executionDate = DateService.convertDateToIDate(receiveExecutionDate);
    const nowDate = DateService.convertDateToIDate(new Date());
    const lastMonthDate = DateService.addMonthsToCurrentDate(-1);
    const lastLastMonthDate = DateService.addMonthsToCurrentDate(-2);

    // 在庫年月日の検証
    // NOTE: 在庫年月日が今月または先月、先々月でない場合にはundefinedを返す
    if (
      (executionDate.month !== nowDate.month || executionDate.year !== nowDate.year) &&
      (executionDate.month !== lastMonthDate.month || executionDate.year !== lastMonthDate.year) &&
      (executionDate.month !== lastLastMonthDate.month || executionDate.year !== lastLastMonthDate.year)
    ) {
      return undefined;
    }

    // 在庫年月日が年月ごとに最新の情報にソートする
    const sortedInventory = response.sort((a, b) => {
      if (a.inventoriedTargetYear > b.inventoriedTargetYear) {
        return -1; // 年が新しい方を前に
      } else if (a.inventoriedTargetYear < b.inventoriedTargetYear) {
        return 1; // 年が古い方を後ろに
      } else {
        // 年が同じ場合、月で比較
        return b.inventoriedTargetMonth - a.inventoriedTargetMonth;
      }
    });

    // 最新の在庫情報を取得する
    // NOTE: 在庫年月日を元に在庫情報を取得する
    for (const inventory of sortedInventory) {
      if (inventory.inventoriedTargetMonth === executionDate.month && inventory.inventoriedTargetYear === executionDate.year) {
        return inventory;
      }
    }

    // 今月または先月の在庫情報がない場合はundefinedを返す;
    return undefined;
  };

  /**
   * GetSimulatedAggregateBusinessInfoをIBusinessに変換する
   * @param response - GetSimulatedAggregateBusinessInfo
   * @param orgId - 組織ID
   */
  public static adapt = (response: GetSimulatedAggregateBusinessInfo, orgId: string): IBusiness => {
    const receiveExecutionDate = DateService.convertUnixTimeToDate(response.executionDate ? response.executionDate / 1000 : 0);
    const latestInventory = this.adaptLatestInventory(receiveExecutionDate, response.inventories);

    return BusinessFactory.create({
      businessId: response.aggregateBusinessId,
      organizationId: orgId,
      MOQ: response.moq,
      manufacturer: response.manufacturer,
      orderPackage: response.orderPackage,
      leadTime: response.leadTime,
      leadTimeUnit: response.leadTimeUnit,
      businessName: response.businessName,
      unit: response.unit,
      salesResults: this.adaptSalesResult(response.salesResults),
      arrivals: this.adaptArrival(response.leadTime, response.arrivals),
      customerAnnounce: this.adaptCustomerAnnounce(response.customerAnnounces),
      customerAnnounceDaily: this.adaptCustomerAnnounceDaily(response.customerAnnounceDaily),
      supplierAnnounce: this.adaptSupplierAnnounce(response.supplierAnnounces),
      warehouse: response.warehouse,
      package: response.package.toString(),
      businessNumber: response.businessNumber,
      supplierName: response.supplierName,
      note: response.aggregateBusinessNote,
      recommend: this.adaptOrderToIRecommendOrderArrival(response.leadTime, response.orders),
      executionDate: receiveExecutionDate,
      stdInventoryIndices: {
        標準在庫指数: {
          min: response.stdInventoryIndicesMin,
          max: response.stdInventoryIndicesMax,
          option: {
            unit: response.stdInventoryIndicesUnit,
          },
        },
      },
      inventory: latestInventory?.inventoryAmount, // 直近の在庫情報がない場合はundefinedを返す
      mvpInventory: response.inventories?.map((inventory): IMvpInventory => {
        return {
          amount: inventory.inventoryAmount ?? 0,
          year: inventory.inventoriedTargetYear ?? 0,
          month: inventory.inventoriedTargetMonth ?? 0,
        };
      }),
      lastCheckedUser: response.lastCheckedUser
        ? UserAccountFactory.create({ email: response.lastCheckedUser.id, name: response.lastCheckedUser.name })
        : undefined,
      lastCheckedAt: response.lastCheckedAt ? DateService.convertMSUnixTimeToDate(response.lastCheckedAt) : undefined,
      aggregateBusinessResponsibleUsers: response.aggregateBusinessResponsibleUsers
        ? response.aggregateBusinessResponsibleUsers.map((user) => UserAccountFactory.create({ email: user.id, name: user.name }))
        : [],
    });
  };
}
